import Flex from "common/Boxes/Flex";
import CopyToClipboardIcon from "common/CopyToClipboard/CopyToClipboard";
import Title from "common/Typography/Title";
import React from "react";
import Tooltip from "common/Tooltips/Tooltip.tsx";
import { copyCodeToClipboard, openMessage } from "utils/misc.tsx";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import { useTheme } from "styled-components";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";
import { Copy } from "lucide-react";
import LookIcon from "common/Icons/LookIcon.tsx";

interface TradingViewUUIDProps {
  uuid?: string;
  name: string;
}

const TradingViewUUID = ({ uuid, name }: TradingViewUUIDProps) => {
  const theme = useTheme();
  const { isReadOnly } = useSubUserPermissions();

  if (isReadOnly) return null;

  const getUuid = () => {
    let jsonResult: unknown;
    try {
      if (uuid) {
        jsonResult = JSON.parse(uuid);
      }
    } catch {
      jsonResult = undefined;
    }

    const content = (
      <Title level={5} $ellipsis={true}>
        {uuid ?? "OFF"}
      </Title>
    );

    if (jsonResult) {
      const formattedJson = JSON.stringify(jsonResult, null, 4).replace(
        /": /g,
        '":  '
      );

      const tooltipContent = (
        <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <pre
            style={{
              margin: 0,
              fontSize: "12px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {formattedJson}
          </pre>
        </div>
      );

      return (
        <Tooltip title={tooltipContent} styles={{ root: { maxWidth: 350 } }}>
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <Flex
      $justifyContent="flex-start"
      $alignItems="center"
      style={{ width: "100%", cursor: "pointer" }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        if (uuid) {
          copyCodeToClipboard(uuid).catch(() => {});
          openMessage(getCopyMessage(name), "info");
        }
      }}
    >
      {uuid && (
        <LookIcon
          classic={
            <CopyToClipboardIcon
              name={name}
              style={{ margin: "0 8px 0 0", color: theme.colors.primaryText }}
              text={uuid}
            />
          }
          // New look: a line icon; a click bubbles to the row, which copies.
          newLook={
            <Copy
              size={16}
              strokeWidth={1.75}
              style={{
                flex: "none",
                marginRight: 8,
                color: theme.colors.secondaryText,
              }}
            />
          }
        />
      )}
      {getUuid()}
    </Flex>
  );
};

export default TradingViewUUID;
